import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTip, ResponsiveContainer, Cell } from 'recharts';
import TeachingTooltip from '@/components/TeachingTooltip';

const toPolar = (r, deg) => ({ re: r * Math.cos((deg * Math.PI) / 180), im: r * Math.sin((deg * Math.PI) / 180) });
const cmag   = (z) => Math.sqrt(z.re * z.re + z.im * z.im);
const cadd   = (a, b) => ({ re: a.re + b.re, im: a.im + b.im });
const cmul   = (a, b) => ({ re: a.re * b.re - a.im * b.im, im: a.re * b.im + a.im * b.re });
const cdiv   = (a, b) => { const d = b.re * b.re + b.im * b.im; return { re: (a.re * b.re + a.im * b.im) / d, im: (a.im * b.re - a.re * b.im) / d }; };
const cinv   = (z) => cdiv({ re: 1, im: 0 }, z);
const cpar   = (a, b) => cdiv(cmul(a, b), cadd(a, b));  // parallel combination

const FAULT_TYPES = ['SLG', 'LL', 'DLG', '3PH'];
const SEQ_COLORS  = { I1: '#0d9488', I2: '#ea580c', I0: '#7c3aed', If: '#0f172a' };

const NumSlider = ({ label, value, onChange, min, max, step = 0.01, tooltip }) => (
  <TeachingTooltip {...tooltip}>
    <div className="flex flex-col gap-1">
      <div className="flex justify-between items-baseline">
        <label className="text-[10px] font-semibold text-slate-500 dark:text-slate-500 uppercase tracking-wide">{label}</label>
        <span className="text-xs font-mono text-teal-700 dark:text-teal-400 font-bold">{value.toFixed(2)}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full accent-teal-600 h-1.5" />
    </div>
  </TeachingTooltip>
);

const FaultCurrentPreviewCalc = () => {
  const [vf, setVf]     = useState(1.0);
  const [z1m, setZ1m]   = useState(0.15);
  const [z1a, setZ1a]   = useState(80);
  const [z2m, setZ2m]   = useState(0.15);
  const [z2a, setZ2a]   = useState(80);
  const [z0m, setZ0m]   = useState(0.50);
  const [z0a, setZ0a]   = useState(72);
  const [fault, setFault] = useState('SLG');

  const Vf = { re: vf, im: 0 };
  const Z1 = toPolar(z1m, z1a);
  const Z2 = toPolar(z2m, z2a);
  const Z0 = toPolar(z0m, z0a);

  let I1, I2, I0, If_phase;
  switch (fault) {
    case 'SLG': {
      const Ztotal = cadd(cadd(Z1, Z2), Z0);
      I1 = cdiv(Vf, Ztotal);
      I2 = { ...I1 };
      I0 = { ...I1 };
      If_phase = { re: I1.re * 3, im: I1.im * 3 };
      break;
    }
    case 'LL': {
      const Ztotal = cadd(Z1, Z2);
      I1 = cdiv(Vf, Ztotal);
      I2 = { re: -I1.re, im: -I1.im };
      I0 = { re: 0, im: 0 };
      If_phase = { re: cmag(I1) * Math.sqrt(3), im: 0 };
      break;
    }
    case 'DLG': {
      const Z2parZ0 = cpar(Z2, Z0);
      const Ztotal  = cadd(Z1, Z2parZ0);
      I1 = cdiv(Vf, Ztotal);
      I2 = cmul({ re: -1, im: 0 }, cdiv(cmul(I1, Z0), cadd(Z2, Z0)));
      I0 = cmul({ re: -1, im: 0 }, cdiv(cmul(I1, Z2), cadd(Z2, Z0)));
      If_phase = { re: cmag(I0) * 3, im: 0 };
      break;
    }
    default: { // 3PH
      I1 = cdiv(Vf, Z1);
      I2 = { re: 0, im: 0 };
      I0 = { re: 0, im: 0 };
      If_phase = { ...I1 };
    }
  }

  const data = [
    { name: '|I\u2081|',  val: cmag(I1),       color: SEQ_COLORS.I1 },
    { name: '|I\u2082|',  val: cmag(I2),       color: SEQ_COLORS.I2 },
    { name: '|I\u2080|',  val: cmag(I0),       color: SEQ_COLORS.I0 },
    { name: '|If|',  val: cmag(If_phase), color: '#0f172a' },
  ];

  return (
    <div data-testid="fault-current-preview-calc" className="space-y-5">
      <p className="text-xs text-slate-500 dark:text-slate-400">
        Enter the Thevenin sequence impedances at the fault bus (from the Thevenin calculator above)
        and compute fault currents for any fault type.
      </p>

      {/* Fault type selector */}
      <div className="flex flex-wrap gap-2">
        {FAULT_TYPES.map((ft) => (
          <button key={ft} data-testid={`fault-preview-${ft}`} onClick={() => setFault(ft)}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors border ${
              fault === ft
                ? 'bg-teal-600 text-white border-teal-600'
                : 'border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:border-teal-400'
            }`}>{ft}</button>
        ))}
      </div>

      {/* Sliders */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <NumSlider label="Vf (pu)" value={vf} onChange={setVf} min={0.5} max={1.1} step={0.05}
          tooltip={{ title: 'Pre-Fault Voltage', whatThis: 'The voltage at the fault bus immediately before the fault (Thevenin source voltage). Standard assumption: Vf = 1.0 pu.', tryThis: 'Keep at 1.0 pu for standard fault studies. Reduce to 0.9 pu to model a pre-existing voltage sag.', whyMatters: 'Fault current is proportional to Vf — a 10% voltage sag reduces fault current by 10%.', connectedTo: 'All fault current results' }} />
        <NumSlider label="|Z\u2081_Th| pu" value={z1m} onChange={setZ1m} min={0.05} max={1.0}
          tooltip={{ title: 'Positive-Sequence Thevenin Z', whatThis: 'The total positive-sequence impedance from source to fault bus (Z1s + Z_T + Z1_line).', tryThis: 'Reduce to 0.05 pu to model a bolted fault at the substation bus (very high fault current).', whyMatters: 'Small Z1 means large fault current and high mechanical/thermal stress on equipment.', connectedTo: '|I1|, fault current bar chart' }} />
        <NumSlider label="|Z\u2082_Th| pu" value={z2m} onChange={setZ2m} min={0.05} max={1.0}
          tooltip={{ title: 'Negative-Sequence Thevenin Z', whatThis: 'Typically equal to Z1_Th for static systems. May differ near large rotating machines.', tryThis: 'Keep equal to Z1 for most distribution studies. Reduce slightly for systems with synchronous generators close to the fault.', whyMatters: 'Z2 appears in series (SLG) or parallel (DLG) combinations. Smaller Z2 increases SLG and LL fault current.', connectedTo: '|I2|, fault current bar chart' }} />
        <NumSlider label="|Z\u2080_Th| pu" value={z0m} onChange={setZ0m} min={0.05} max={2.0}
          tooltip={{ title: 'Zero-Sequence Thevenin Z', whatThis: 'The total zero-sequence impedance. For overhead feeders with ground wires, Z0 \u2248 3\u00d7 Z1. For cables, Z0 \u2248 Z1. Depends heavily on transformer grounding configuration.', tryThis: 'Set Z0 = 999 (open) to model an ungrounded system where SLG fault current = 0.', whyMatters: 'Z0 is the single most important parameter for SLG fault current. Changing Z0 from 0.3 to 3.0 can reduce SLG fault current by 10\u00d7.', connectedTo: '|I0| (SLG, DLG), fault current bar chart' }} />
      </div>

      {/* Results */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          {[
            { label: '|I\u2081| (pos-seq)', val: cmag(I1), c: '#0d9488' },
            { label: '|I\u2082| (neg-seq)', val: cmag(I2), c: '#ea580c' },
            { label: '|I\u2080| (zer-seq)', val: cmag(I0), c: '#7c3aed' },
            { label: '|If| (fault current)', val: cmag(If_phase), c: '#0f172a' },
          ].map(({ label, val, c }) => (
            <div key={label} className="flex justify-between items-center py-1.5 border-b border-slate-100 dark:border-slate-800">
              <span className="text-xs font-mono" style={{ color: c }}>{label}</span>
              <span className="text-sm font-bold font-mono" style={{ color: c }}>{val.toFixed(3)} pu</span>
            </div>
          ))}
        </div>
        <ResponsiveContainer width="100%" height={160}>
          <BarChart data={data} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
            <XAxis dataKey="name" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} />
            <RechartsTip formatter={(v) => [`${v.toFixed(3)} pu`, 'Magnitude']} />
            <Bar dataKey="val" radius={[3, 3, 0, 0]}>
              {data.map((entry, i) => (
                <Cell key={i} fill={entry.color} fillOpacity={0.85} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default FaultCurrentPreviewCalc;
